import { Alert, Stack } from "@mui/material";
import AppDialog from "components/common/AppDialog";
import CustomButton from "components/common/CustomButton";
import { useCreateWebhookTriggerMutation } from "features/integrationsBeta/webhookTriggers";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import { useState } from "react";
import { formatApiError } from "utils/formatApiError";
import WebhookSetupReveal from "./WebhookSetupReveal";
import WebhookTriggerFields from "./WebhookTriggerFields";

interface Props {
  open: boolean;
  onClose: () => void;
  integrationId: number;
}

interface CreatedResult {
  webhookUrl: string;
  secretKey: string;
}

/**
 * Create an inbound webhook trigger from just Name + Provider. The backend
 * auto-provisions a dedicated WEBHOOK connection and generates the Secret-Key,
 * which is shown once on success for pasting into the provider UI.
 */
export default function WebhookTriggerCreateDialog({
  open,
  onClose,
  integrationId,
}: Props) {
  const dispatch = useAppDispatch();
  const [createTrigger, { isLoading }] = useCreateWebhookTriggerMutation();

  const [name, setName] = useState("");
  const [provider, setProvider] = useState("");
  const [apiError, setApiError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedResult | null>(null);

  const reset = () => {
    setName("");
    setProvider("");
    setApiError(null);
    setCreated(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleCreate = async () => {
    if (!provider) return;
    try {
      const result = await createTrigger({
        integration: integrationId,
        provider,
        ...(name.trim() ? { name: name.trim() } : {}),
      }).unwrap();
      setCreated({
        webhookUrl: result.webhook_url,
        secretKey: result.secret_key ?? "",
      });
    } catch (err) {
      setApiError(
        formatApiError(
          (err as { data?: unknown })?.data,
          "Could not create the webhook trigger.",
        ),
      );
    }
  };

  const handleDone = () => {
    dispatch(
      pushNotification({
        severity: "success",
        message: "Webhook trigger created.",
      }),
    );
    handleClose();
  };

  return (
    <AppDialog
      open={open}
      onClose={handleClose}
      title={created ? "Webhook trigger created" : "Add webhook trigger"}
      maxWidth="sm"
      showCloseIcon
      actions={
        created ? (
          <CustomButton fullWidth={false} onClick={handleDone}>
            Done
          </CustomButton>
        ) : (
          <>
            <CustomButton fullWidth={false} onClick={handleClose}>
              Cancel
            </CustomButton>
            <CustomButton
              fullWidth={false}
              onClick={handleCreate}
              disabled={isLoading || !provider}
            >
              Create
            </CustomButton>
          </>
        )
      }
    >
      {created ? (
        <Stack spacing={2} sx={{ mt: 1 }}>
          <WebhookSetupReveal
            webhookUrl={created.webhookUrl}
            secretKey={created.secretKey}
            oneTime
          />
        </Stack>
      ) : (
        <Stack spacing={2} sx={{ mt: 1 }}>
          <WebhookTriggerFields
            name={name}
            onNameChange={setName}
            provider={provider}
            onProviderChange={setProvider}
          />
          {apiError && <Alert severity="error">{apiError}</Alert>}
        </Stack>
      )}
    </AppDialog>
  );
}
